import React, { useEffect, useState, createElement } from 'react';
import { History, Download, FileText, Building2, Users, UtensilsCrossed, Settings } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { Pagination } from '../components/ui/Pagination';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { getSupabase } from '../lib/supabase';
export function ActivityLogPage() {
  const {
    logs
  } = useActivityLog();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('Todos');
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Sincronización en tiempo real
  useEffect(() => {
    const client = getSupabase();
    if (!client) return;
    const subscription = client.channel('activity-logs-channel').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'activity_logs'
    }, () => {
      console.log('Activity log updated, context will reload...');
      // El contexto ActivityLogContext ya maneja la recarga
    }).subscribe();
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.action.toLowerCase().includes(searchTerm.toLowerCase()) || log.details.toLowerCase().includes(searchTerm.toLowerCase()) || log.user.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'Todos' || log.type === typeFilter.toLowerCase();
    return matchesSearch && matchesType;
  });
  // Paginación
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const paginatedLogs = filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset a primera página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter]);
  const getIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <FileText size={16} data-id="element-691" />;
      case 'company':
        return <Building2 size={16} data-id="element-692" />;
      case 'employee':
        return <Users size={16} data-id="element-693" />;
      case 'menu':
        return <UtensilsCrossed size={16} data-id="element-694" />;
      default:
        return <Settings size={16} data-id="element-695" />;
    }
  };
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'order':
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
      case 'company':
        return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800';
      case 'employee':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800';
      case 'menu':
        return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
    }
  };
  const exportLogs = () => {
    let csv = 'Registro de Actividad - Cricket\n\n';
    csv += 'Fecha,Usuario,Acción,Detalles,Tipo\n';
    filteredLogs.forEach(log => {
      csv += `${log.timestamp},${log.user},"${log.action}","${log.details}",${log.type}\n`;
    });
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `registro-actividad-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-id="element-696">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-697">
        <div data-id="element-698">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-699">
            Registro de Actividad
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-700">
            Historial completo de acciones administrativas
          </p>
        </div>
        <Button leftIcon={<Download size={18} data-id="element-702" />} onClick={exportLogs} disabled={filteredLogs.length === 0} data-id="element-701">
          Exportar Registro
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-703">
        <SearchBar placeholder="Buscar en registro..." className="w-full sm:flex-1" onSearch={setSearchTerm} data-id="element-704" />
        <FilterDropdown label="Tipo" value={typeFilter} options={['Todos', 'Order', 'Company', 'Employee', 'Menu', 'System']} onChange={setTypeFilter} className="w-full sm:w-48" data-id="element-705" />
      </div>

      <Card data-id="element-706">
        {paginatedLogs.length === 0 ? <div className="text-center py-20" data-id="element-707">
            <History size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-708" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-709">
              No hay actividad registrada
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-710">
              {searchTerm || typeFilter !== 'Todos' ? 'No se encontraron resultados con los filtros aplicados' : 'Las acciones administrativas aparecerán aquí'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-711">
            <table className="w-full text-left border-collapse" data-id="element-712">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-713">
                <tr data-id="element-714">
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-715">
                    Fecha y Hora
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-716">
                    Usuario
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-717">
                    Acción
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-718">
                    Detalles
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-719">
                    Tipo
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-720">
                {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-721">
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 font-mono" data-id="element-722">
                      {log.timestamp}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white" data-id="element-723">
                      {log.user}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white" data-id="element-724">
                      {log.action}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-725">
                      {log.details}
                    </td>
                    <td className="px-4 py-3" data-id="element-726">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getTypeColor(log.type)}`} data-id="element-727">
                        {getIcon(log.type)}
                        {log.type}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}

        {/* Paginación */}
        {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredLogs.length} itemsPerPage={itemsPerPage} data-id="element-728" />}
      </Card>
    </div>;
}